import React from "react";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import { useTheme } from "styled-components";
import PropTypes from "prop-types";

const LockedMultiValueRemove = props =>
  props.data.locked ? null : <components.MultiValueRemove {...props} />;

const LockedMultiValueLabel = props => (
  <components.MultiValueLabel {...props}>
    {props.data.locked && (
      <i className="fa fa-lock mr-1" aria-hidden="true"></i>
    )}
    {props.children}
  </components.MultiValueLabel>
);

const buildStyles = theme => ({
  container: base => ({
    ...base,
    width: "100%",
    maxWidth: "420px",
  }),
  control: (base, state) => ({
    ...base,
    backgroundColor: theme.card,
    border: theme.cardBorder,
    boxShadow: state.isFocused ? `0 0 0 1px ${theme.paginationBtn}` : "none",
    "&:hover": { border: theme.cardBorder },
  }),
  menu: base => ({
    ...base,
    backgroundColor: theme.card,
    border: theme.cardBorder,
    zIndex: 5,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? theme.hoverBtn : theme.card,
    color: theme.text,
    cursor: "pointer",
  }),
  multiValue: (base, state) => ({
    ...base,
    backgroundColor: theme.hoverBtn,
    opacity: state.data.locked ? 0.8 : 1,
  }),
  multiValueLabel: base => ({
    ...base,
    color: theme.text,
  }),
  multiValueRemove: base => ({
    ...base,
    color: theme.text,
    "&:hover": { backgroundColor: theme.paginationBtn, color: theme.card },
  }),
  input: base => ({ ...base, color: theme.text }),
  singleValue: base => ({ ...base, color: theme.text }),
  placeholder: base => ({ ...base, color: theme.disabledText }),
  dropdownIndicator: base => ({ ...base, color: theme.disabledText }),
  clearIndicator: base => ({ ...base, color: theme.disabledText }),
  indicatorSeparator: base => ({ ...base, backgroundColor: theme.disabledText }),
});

const TagPicker = ({ selectedTags, derivedTags, options, onChange }) => {
  const theme = useTheme();
  const shownTags = [...new Set([...selectedTags, ...derivedTags])];
  const value = shownTags.map(tag => ({
    value: tag,
    label: tag,
    locked: derivedTags.includes(tag),
  }));

  const handleChange = selected => {
    const next = (selected || []).map(s => s.value);
    // Only persist a tag if it was already persisted before, or if it's not
    // merely reflecting the live text (i.e. it's a genuine manual pick) -
    // otherwise a currently-typed #hashtag would get stuck in state the
    // moment any unrelated picker interaction happens.
    onChange(next.filter(tag => selectedTags.includes(tag) || !derivedTags.includes(tag)));
  };

  return (
    <CreatableSelect
      isMulti
      value={value}
      options={options.map(tag => ({ value: tag, label: tag }))}
      onChange={handleChange}
      components={{
        MultiValueRemove: LockedMultiValueRemove,
        MultiValueLabel: LockedMultiValueLabel,
      }}
      placeholder="Add tags..."
      styles={buildStyles(theme)}
      className="align-self-start mb-2"
      classNamePrefix="tag-picker"
    />
  );
};

TagPicker.propTypes = {
  selectedTags: PropTypes.arrayOf(PropTypes.string),
  derivedTags: PropTypes.arrayOf(PropTypes.string),
  options: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
};

export default TagPicker;
